import { PortalApiError, portalApiGet } from '@/lib/portal-api';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { fetchPatientMasterSearch, fetchPatientMasterRecord, fetchPatient360Summary, markAppointmentArrived, markAppointmentCheckedIn, revealPatientContact } from '@/dashboard/api';
import SharedRecordSearch, {
  type SharedSearchItem,
  type SharedSearchPage,
} from '@/dashboard/ui/SharedRecordSearch';
import GlobalPatientSearchModal from './GlobalPatientSearchModal';
import RegisterBookingOffer from './RegisterBookingOffer';
import PatientQuickDetails from './PatientQuickDetails';
import { VISIT_CLINIC_LABEL, type Patient360AppointmentWire } from './patient-360-view';
import { RECORD_HEADER_REGISTERED_AT_LABEL } from '@/dashboard/ui/RecordHeader';
import { WorkPanel } from '@/dashboard/ui/MasterDetail';
import { AcButton } from '@/dashboard/ui';
import {
  deskRecordHref,
  type PatientCandidateCard,
  type PatientSearchResponse,
  type DeskRecordOrigin,
} from './patient-master-view';
import { Link } from 'react-router-dom';
import LeadContactRevealControl, { PHONE_MASK } from '@/dashboard/owner/LeadContactReveal';
import { dayStatusChip } from '@/dashboard/appointments/ClinicDayBoard';

/** Reception's approved name minimum does not change identifier searches. */
export function receptionSearchQueryHint(q: string): string | null {
  if (/^[+#\d\s()-]+$/.test(q) || /^[A-Z][A-Z0-9]{0,7}-?\d{1,18}$/i.test(q)) return null;
  return (q.match(/\p{L}/gu)?.length ?? 0) < 3
    ? 'Enter at least 3 letters of the name, a full mobile, UMR or appointment number.' : null;
}
/** The server's rule: names stay in the working clinic; exact identifiers
 *  may find a patient registered at another clinic of the organization. */
/** The LAST-RESORT fallback for a server that does not send
 *  `priorAttendance` at all: an earlier IST day on which this person reached
 *  a clinic (arrived or beyond; any branch), read off the displayed visits.
 *
 *  THE DIRECT STAMP ONLY. A displayed visit may be `lead_linked` -- an
 *  appointment that hangs off one of this person's leads and was never
 *  stamped onto the patient row. The check-in route counts rows carrying a
 *  resolved patient id, and the day board keys on `patient_id IN (linkedIds)`;
 *  neither counts that relationship. Affirming returning from it made the
 *  panel offer a check-in the route then refused, so it is excluded here.
 *  The row is still SHOWN -- display, not eligibility. */
const RETURNING_VISIT_STATUSES: readonly string[] = ['arrived', 'checked_in', 'completed', 'fulfilled'];
const istDay = (ms: number) => new Date(ms + 330 * 60_000).toISOString().slice(0, 10);
export function receptionReturningFact(
  visits: readonly Pick<Patient360AppointmentWire, 'status' | 'scheduledAt' | 'linkage'>[],
  nowMs: number,
): boolean {
  const today = istDay(nowMs);
  return visits.some(visit => visit.linkage === 'direct' &&
    RETURNING_VISIT_STATUSES.includes(visit.status) &&
    Number.isFinite(Date.parse(visit.scheduledAt)) && istDay(Date.parse(visit.scheduledAt)) < today);
}

/** WALK-IN CONTINUATION (explicit state, not truthiness).
 *
 *  After a walk-in is booked from this panel there are two continuations:
 *  a person who may check in HERE stays in the panel for it, and everyone
 *  else takes the ordinary new-patient continuation out of the panel
 *  (changePatient(null) -> onAppointment).
 *
 *  `returning` is the three-valued fact 'returning' | 'new' | 'unknown'. It
 *  must be compared EXPLICITLY: every one of those strings is truthy, so a
 *  boolean test cannot tell a proven NEW patient from a returning one. */
export function walkInContinuesInPanel(
  arrival: string | null,
  returning: 'returning' | 'new' | 'unknown',
): boolean {
  return arrival === 'arrival' && returning !== 'new';
}

/** RETURNING-INTAKE-PARITY-V1 (review item 2): a returning person whose
 *  registration window for THIS visit is still open finishes that window --
 *  the returning shortcut is not offered on top of it.
 *
 *  THE DEFECT THIS CLOSES. The day board already excluded an in-progress
 *  intake (`isReturningCheckInRow`: intakeStatus !== 'in_progress'), but this
 *  panel never looked at `visit.intake` although the wire already carried it.
 *  So the same person, on the same visit, was refused the shortcut on the
 *  board and offered it in the panel. The rule now reads from ONE place.
 *
 *  UNKNOWN IS NOT COMPLETED, AND IT IS NOT IN-PROGRESS EITHER. When intake
 *  evidence is `unavailable` this returns false -- matching the board, which
 *  also only excludes a KNOWN in-progress window -- and the server's own
 *  registration fences still run. Nothing here asserts that an unknown intake
 *  is finished. */
export function returningBlockedByIntake(
  visit: Pick<Patient360AppointmentWire, 'intake'>,
): boolean {
  return visit.intake.availability === 'available' &&
    visit.intake.status === 'in_progress';
}

const savingKey = (patientId: string, appointmentId: number) => `${patientId}:${appointmentId}`;
const personSaving = (held: ReadonlySet<string>, patientId: string) => [...held].some(key => key.startsWith(`${patientId}:`));

export function receptionSearchScopeLine(clinicLabel: string): string {
  return `Names: ${clinicLabel}. Exact mobile, UMR or appointment number: all clinics.`;
}
export const RECEPTION_SEARCH_ALL_CLINICS = 'All clinics';
export function receptionSearchPage(result: PatientSearchResponse, page: number): SharedSearchPage {
  const items: SharedSearchItem[] = result.candidates.map((patient) => ({
    id: `patient:${patient.patientId}`,
    name: patient.fullName,
    identifier: patient.umr,
    contact: patient.contactDisplay ?? 'Hidden',
    contactExpiresAt: patient.contactExpiresAt,
    clinic: patient.registeredClinicLabel ?? 'Unavailable',
    clinicFieldLabel: RECORD_HEADER_REGISTERED_AT_LABEL,
    status:
      patient.status === 'active'
        ? 'Registered patient'
        : patient.status === 'merged'
          ? 'Merged patient'
          : 'Status unavailable',
    summary: 'No appointment selected',
    action: 'Open patient',
  }));
  for (const lead of result.enquiries ?? [])
    items.push({
      id: `lead:${lead.leadId}`,
      name: lead.name ?? 'Name unavailable',
      identifier: lead.registrationStatus === 'unregistered' ? 'Not registered' : 'Unavailable',
      identifierLabel: 'UMR',
      contact: lead.contactDisplay ?? (lead.hasPhone ? PHONE_MASK : 'Not recorded'),
      contactExpiresAt: lead.contactExpiresAt,
      clinic: lead.clinicName ?? 'Unavailable',
      status: 'Lead',
      summary: lead.appointmentPresence === 'none' ? 'No appointment recorded' : lead.appointmentPresence === 'recorded' ? 'Appointment recorded' : 'Appointment unavailable',
      action: 'Continue registration',
    });
  const appointment = result.appointmentMatch;
  if (appointment && page === 0)
    items.push({
      id: `appointment:${appointment.appointmentId}`,
      name: appointment.patientLabel ?? 'Name unavailable',
      identifier: `#${appointment.appointmentId}`,
      identifierLabel: 'Appointment',
      contact: 'Hidden',
      clinic: appointment.clinicLabel ?? 'Unavailable',
      clinicFieldLabel: VISIT_CLINIC_LABEL,
      status: dayStatusChip(appointment.status).label,
      summary: appointment.doctorLabel ?? 'Doctor unavailable',
      action: 'Open appointment',
    });
  return {
    items,
    total: result.complete === true && typeof result.total === 'number' ? result.total : null,
    page,
    pageSize: result.pageSize ?? 25,
    complete: result.complete === true,
    notice: result.leadNameLimitReached ? 'Showing up to 5 matching leads. Refine the name for more.' : undefined,
    sharedPhone: result.searchMode === 'phone' && (result.total ?? items.length) > 1,
    // Only a name search is held to the working clinic.
    scopeLabel: result.searchMode === 'name' && result.appointmentMatch === undefined ? undefined : RECEPTION_SEARCH_ALL_CLINICS,
  };
}
export default function ReceptionSearch({
  scopeKey,
  clinicId,
  clinicLabel,
  disabled,
  panelTarget,
  onPanelChange,
  onAppointment,
  onChanged,
  recordOrigin = 'walkin',
}: {
  recordOrigin?: DeskRecordOrigin;
  scopeKey: string;
  clinicId: number | null;
  clinicLabel: string;
  disabled: boolean;
  panelTarget: HTMLElement | null;
  onPanelChange: (open: boolean) => void;
  onAppointment: (id: number, iso: string) => void;
  onChanged: () => void;
}): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [scopeMatches, setScopeMatches] = useState(() => {
    try {
      return sessionStorage.getItem('reception-search-scope') === scopeKey;
    } catch {
      return false;
    }
  });
  const [query, setQuery] = useState(scopeMatches ? (params.get('searchQuery') ?? '') : ''),
    [open, setOpen] = useState(false),
    [registration, setRegistration] = useState<{ query: string; leadId?: number; page?: number } | null>(null);
  const patientId = scopeMatches ? params.get('searchPatient') : null;
  const [patient, setPatient] = useState<PatientCandidateCard | null>(null),
    [error, setError] = useState<string | null>(null),
    [arrival, setArrival] = useState<'arrival' | 'appointment' | null>(null),
    [history, setHistory] = useState<'visits' | 'payments' | null>(null),
    [busy, setBusy] = useState(false);
  const [recordAccess, setRecordAccess] = useState<{patientId: string; scopeKey: string; allowed: boolean} | null>(null);
  const [mobileContactId, setMobileContactId] = useState<number | null>(null);
  const [activeVisits, setActiveVisits] = useState<readonly Patient360AppointmentWire[] | null>(null);
  const [visitReadError, setVisitReadError] = useState(false);
  const [visitRetry, setVisitRetry] = useState(0);
  // Returning-patient check-in (Ravi, 16 Oct 2026): set from this person's
  // earlier visits; the server re-verifies before saving anything.
  /** RETURNING-ELIGIBILITY-AUTHORITY-V1: three-valued ON PURPOSE. A boolean
   *  forced this panel to pick between two wrong answers when its own list
   *  was capped -- claim "returning" for everyone, or deny it for someone the
   *  board had already badged. `unknown` is the honest third answer: it makes
   *  no claim and relaxes nothing. */
  const [returning, setReturning] = useState<'returning' | 'new' | 'unknown'>('new');
  // Saves in flight, keyed by the person and visit that started them, so one
  // person's held save never blocks or relabels another person's buttons.
  const [visitSaving, setVisitSaving] = useState<ReadonlySet<string>>(() => new Set());
  const [visitNotice, setVisitNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const generation = useRef(0),
    latest = useRef({ scopeKey, disabled, patientId });
  latest.current = { scopeKey, disabled, patientId };
  const disclosedPatients = useRef(new Map<string, PatientCandidateCard>());
  const searchContext = useRef({scopeKey,query,disabled});searchContext.current={scopeKey,query,disabled};
  useEffect(()=>{disclosedPatients.current.clear();},[scopeKey,query,disabled]);
  useEffect(()=>{const timer=setInterval(()=>{
    for(const [id,card] of disclosedPatients.current) if(Date.parse(card.contactExpiresAt??'')<=Date.now())disclosedPatients.current.delete(id);
    setPatient(current=>{if(!current?.contactExpiresAt||Date.parse(current.contactExpiresAt)>Date.now())return current;const {contactDisplay,contactExpiresAt,...rest}=current;return rest;});
  },1000);return()=>clearInterval(timer);},[]);
  const changed = useRef(onChanged); changed.current = onChanged;
  const bookingSaved = useCallback(() => changed.current(), []);
  const patientPages = useRef(new Map<string, number>());
  const leadPages = useRef(new Map<number, number>());
  const appointmentRows = useRef<PatientSearchResponse['appointmentMatch'][]>([]);
  const focusHeader = () =>
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>('[data-testid="home-global-search-input"]')?.focus(),
    );
  const canOpenFullRecord = recordAccess?.allowed === true && recordAccess.patientId === patientId && recordAccess.scopeKey === scopeKey && !disabled;
  const changePatient = (id: string | null) => {
    const next = new URLSearchParams(window.location.search);
    // Child selections belong to the patient being left, never the next search result.
    for (const key of ['quickVisit', 'quickDocument', 'billingDestination']) next.delete(key);
    if (id) {
      try {
        sessionStorage.setItem('reception-search-scope', scopeKey);
      } catch {
        /* no cross-session restore */
      }
      setScopeMatches(true);
      next.set('searchPatient', id);
      next.set('searchQuery', query);
      next.set('searchPage', String(patientPages.current.get(id) ?? 0));
      next.delete('quickAppointment');
      next.delete('recordPatient');
      next.delete('quickDetail');
    } else {
      next.delete('searchPatient');
      next.delete('searchQuery');
      next.delete('searchPage');
    }
    setParams(next);
  };
  const close = () => {
    if (busy) return;
    generation.current++;
    changePatient(null);
    setPatient(null);
    setRecordAccess(null);
    setArrival(null);
    setHistory(null);
    setError(null);
    focusHeader();
  };
  const load = useCallback(
    async (q: string, page: number) => {
      if (clinicId === null || disabled) throw new Error('Search unavailable');
      const result = await fetchPatientMasterSearch(q, undefined, { clinicId, page });
      if(searchContext.current.scopeKey!==scopeKey||searchContext.current.query.trim()!==q||searchContext.current.disabled)throw new Error('Search changed');
      disclosedPatients.current.clear();
      for (const card of result.candidates) patientPages.current.set(card.patientId, page);
      for(const card of result.candidates) if(card.contactDisplay && Date.parse(card.contactExpiresAt??'')>Date.now())disclosedPatients.current.set(card.patientId,card);
      appointmentRows.current = [result.appointmentMatch];
      for (const lead of result.enquiries ?? []) leadPages.current.set(lead.leadId, page);
      return receptionSearchPage(result, page);
    },
    [clinicId, disabled, scopeKey],
  );
  useEffect(() => {
    onPanelChange(patientId !== null && !disabled);
    return () => onPanelChange(false);
  }, [patientId, disabled, onPanelChange]);
  useEffect(() => {
    const serial = ++generation.current;
    setPatient(null);
    setMobileContactId(null);
    setError(null);
    setArrival(null);
    setHistory(null);
    setActiveVisits(null); setVisitReadError(false); setReturning('new');
    if (!patientId || disabled) return;
    fetchPatient360Summary(patientId).then(result => {
      if (serial !== generation.current || latest.current.scopeKey !== scopeKey || latest.current.disabled) return;
      if (result.patientId !== patientId || !Array.isArray(result.todayAppointments)) throw new Error('Visit scope unavailable');
      // RETURNING-ELIGIBILITY-AUTHORITY-V1 (review item 3).
      //
      // THE DEFECT. `recentVisits` is the TEN rows this panel DISPLAYS --
      // `pastAll` sliced to PATIENT_360_RECENT_VISIT_LIMIT. Ten cancelled,
      // no-show or merely booked rows can sit in front of a genuine earlier
      // attendance, and attended-then-rebooked history never appears in the
      // list at all. Answering "not returning" from that slice is a FALSE
      // NEGATIVE: the day board said Returning for the same person, and this
      // panel then offered no returning check-in.
      //
      // THE ANSWER IS THE SERVER'S, and it is now asked for: the patient-360
      // reader derives `priorAttendance` from its full bounded past set plus
      // readClinicRebookedAttendance -- the same retention reader the day
      // board and the check-in route use.
      //
      // AND UNCERTAINTY IS SHOWN, NOT GUESSED. An earlier revision of this
      // fix treated a capped list as "returning", which would have labelled
      // EVERY capped patient returning -- a false positive traded for a false
      // negative, and a claim the panel cannot support. The state is three-
      // valued instead: proven returning, proven new, or UNKNOWN. Unknown
      // makes no claim on the screen and never relaxes a fence; the server
      // re-verifies the earlier visit on every check-in and refuses with
      // returning_patient_not_established, so eligibility is enforced there
      // and not inferred here.
      //
      // THREE DISTINCT SERVER ANSWERS, and `null` is not the same as absent.
      // `undefined` means an older server sent no field at all. `null` means
      // THIS server tried and could not read the fact -- an explicit
      // unavailable. An earlier revision let null fall through to the list
      // heuristic, so null + an empty UNCAPPED list resolved to 'new': the
      // action was hidden and no uncertainty was shown, on the strength of an
      // answer the server had explicitly declined to give.
      const authoritative = result.priorAttendance;
      setReturning(
        authoritative === 'returning' ? 'returning'
        : authoritative === 'new' ? 'new'
        : authoritative === null ? 'unknown'
        // No field at all: the displayed list can still PROVE a returning
        // patient, but it can never DISPROVE one once it is capped.
        : receptionReturningFact(result.recentVisits ?? [], Date.now()) ? 'returning'
        : result.recentVisitsCapped === true ? 'unknown'
        : 'new');
      setActiveVisits(result.todayAppointments.filter(visit => visit.clinicId === clinicId &&
        !['fulfilled','completed','cancelled','no_show','rescheduled'].includes(visit.status)));
    }).catch(() => { if (serial === generation.current) setVisitReadError(true); });
    portalApiGet<Awaited<ReturnType<typeof fetchPatientMasterRecord>> & {canOpenFullRecord?: boolean}>(`/patient-master/search/open-patient?${new URLSearchParams({patientId, q: params.get('searchQuery') ?? '', clinicId: String(clinicId), page: params.get('searchPage') ?? '0'})}`)
      .then(async (result) => {
        if (
          serial !== generation.current ||
          latest.current.scopeKey !== scopeKey ||
          latest.current.disabled
        )
          return;
        if (result.patient.patientId !== patientId) {
          setError('Patient is not available in this clinic.');
          return;
        }
        let disclosed = disclosedPatients.current.get(patientId);
        // Reopening after expiry performs a fresh authorized, audited search.
        // Never extend the old disclosure or trust contact data from record reads.
        if ((!disclosed || Date.parse(disclosed.contactExpiresAt ?? '') <= Date.now()) && clinicId !== null) {
          try {
            const fresh = await fetchPatientMasterSearch(result.patient.umr, 'umr', {clinicId, page: 0});
            disclosed = fresh.candidates.find(card => card.patientId === patientId && card.registeredClinicId === clinicId);
          } catch { disclosed = undefined; }
        }
        if (serial !== generation.current || latest.current.scopeKey !== scopeKey || latest.current.disabled) return;
        const {contactDisplay: ignoredContact, contactExpiresAt: ignoredExpiry, ...record} = result.patient;
        const permitted = disclosed?.contactDisplay && Date.parse(disclosed.contactExpiresAt ?? '') > Date.now();
        if (permitted) disclosedPatients.current.set(patientId, disclosed!);
        setRecordAccess({patientId, scopeKey, allowed: result.canOpenFullRecord ?? result.patient.registeredClinicId === clinicId});
        setPatient(permitted ? {...record, contactDisplay: disclosed!.contactDisplay, contactExpiresAt: disclosed!.contactExpiresAt} : record);
        const mobile = result.contactPoints?.find(point => point.type === 'mobile' && point.hasValue && point.isPrimary);
        setMobileContactId(mobile?.id ?? null);
        requestAnimationFrame(() =>
          document
            .querySelector<HTMLElement>('[data-testid="search-patient-quick-view"] button')
            ?.focus(),
        );
      })
      .catch(() => {
        if (serial === generation.current)
          setError('Patient is no longer available. Return to search.');
      });
    return () => {
      generation.current++;
    };
  }, [patientId, scopeKey, clinicId, disabled, visitRetry]);
  useEffect(() => { setVisitNotice(null); }, [patientId]);
  const visitAction = (visit: Patient360AppointmentWire, kind: 'arrived' | 'checked_in') => {
    if (!patientId || busy || personSaving(visitSaving, patientId)) return;
    const key = savingKey(patientId, visit.appointmentId);
    // The result belongs to the person, scope and panel generation that started
    // it -- the same guard as the reads above. After a switch or close it sets
    // no notice and reloads nothing for whoever is open now.
    const serial = generation.current, startedScope = scopeKey, startedPatient = patientId;
    const current = () => serial === generation.current && latest.current.scopeKey === startedScope &&
      !latest.current.disabled && latest.current.patientId === startedPatient;
    setVisitSaving(held => new Set(held).add(key));
    setVisitNotice(null);
    const run = kind === 'arrived'
      ? markAppointmentArrived(visit.appointmentId)
      // Selecting this person and pressing their named Check in is the identity check.
      : markAppointmentCheckedIn(visit.appointmentId, true, true);
    run.then(() => {
      // The saved visit changed the day: refresh the board through the latest callback.
      changed.current();
      if (!current()) return;
      setVisitNotice({ tone: 'success', text: kind === 'arrived' ? `Arrived: appointment #${visit.appointmentId}. Check in when ready.` : `Checked in: appointment #${visit.appointmentId}. The doctor sees the visit on their Today.` });
      setVisitRetry(n => n + 1);
    }).catch((err: unknown) => {
      if (!current()) return;
      const code = err instanceof PortalApiError ? err.code : null;
      setVisitNotice({ tone: 'error', text: code === 'returning_patient_not_established'
        ? 'No earlier visit is on record for this person. Open the appointment to complete registration, then check in.'
        : err instanceof Error && err.message ? err.message : 'The visit could not be updated. Review the appointment.' });
      setVisitRetry(n => n + 1);
    }).finally(() => setVisitSaving(held => { const next = new Set(held); next.delete(key); return next; }));
  };
  // An access/session change remounts this adapter at its owner; no rows or queries survive it.
  const pick = (item: SharedSearchItem) => {
    if (item.id.startsWith('patient:')) {
      changePatient(item.id.slice(8));
      return;
    }
    if (item.id.startsWith('lead:')) {
      setRegistration({ query, leadId: Number(item.id.slice(5)), page: leadPages.current.get(Number(item.id.slice(5))) ?? 0 });
      return;
    }
    const appointment = appointmentRows.current.find(
      (row) => row?.appointmentId === Number(item.id.slice(12)),
    );
    if (appointment) onAppointment(appointment.appointmentId, appointment.appointmentTimeIso);
  };
  const panel =
    patientId && !disabled ? (
      <WorkPanel
        eyebrow="Details"
        title={history === 'visits' ? 'All visits' : history === 'payments' ? (params.has('quickDocument') ? 'Receipt' : 'All payments') : arrival === 'arrival' ? 'Record arrival' : arrival === 'appointment' ? 'Book appointment' : 'Patient quick view'}
        onClose={close}
        closeDisabled={busy}
        testId="search-patient-quick-view"
      >
        {error ? (
          <p role="alert">{error}</p>
        ) : !patient ? (
          <p role="status">Loading patient…</p>
        ) : history && clinicId !== null ? (
          <PatientQuickDetails key={`${patient.patientId}:${history}:${clinicId}`} patientId={patient.patientId}
            clinicId={clinicId} detail={history} patientName={patient.fullName} onBack={() => { setHistory(null); requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-testid="search-quick-${history}"]`)?.focus()); }} />
        ) : arrival ? (
          <RegisterBookingOffer
            patientBookingDetails
            bookingIntent={arrival}
            bookingPresentation={arrival === 'appointment'}
            patientUmr={patient.umr}
            patientId={patient.patientId}
            patientName={patient.fullName}
            headline={patient.fullName}
            leadId={null}
            clinicId={clinicId}
            onBusyChange={setBusy}
            onBookingSaved={bookingSaved}
            onSkip={() => setArrival(null)}
            {...(walkInContinuesInPanel(arrival, returning) ? { bookedActionLabel: 'Continue to check in' } : {})}
            onBooked={(id, iso) => {
              // A returning person's walk-in stays in this panel for its Check in.
              if (walkInContinuesInPanel(arrival, returning)) {
                setArrival(null);
                onChanged();
                setVisitNotice({ tone: 'success', text: `Walk-in booked: appointment #${id}. Check in when ready.` });
                setVisitRetry(n => n + 1);
                return;
              }
              changePatient(null);
              onChanged();
              onAppointment(id, iso);
            }}
          />
        ) : (
          <>
            <section className="aps-quick">
              <h3>
                {canOpenFullRecord ? <Link
                  to={deskRecordHref(patient.patientId, recordOrigin, window.location.search)}
                  aria-label={`Open full record for ${patient.fullName}`}
                >
                  {patient.fullName}
                </Link> : patient.fullName}
              </h3>
              <dl className="today-home__quick-facts">
                <div>
                  <dt>UMR</dt>
                  <dd>{patient.umr}</dd>
                </div>
                <div>
                  <dt>Mobile</dt>
                  <dd>{patient.contactDisplay && Date.parse(patient.contactExpiresAt??'')>Date.now() ? patient.contactDisplay : mobileContactId === null ? 'Hidden' : <LeadContactRevealControl
                    key={`${scopeKey}:${patient.patientId}:${mobileContactId}`}
                    availability="available" polished requireSelection
                    reveal={purpose => revealPatientContact(patient.patientId, mobileContactId, purpose)}
                  />}</dd>
                </div>
                <div>
                  <dt>Registered at</dt>
                  <dd>{patient.registeredClinicLabel ?? 'Unavailable'}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>
                    {patient.status === 'active' ? 'Registered patient' : 'Status unavailable'}
                  </dd>
                </div>
              </dl>
              {/* The CLAIM is made only when it is proven. `unknown` says what
                  it does not know instead; silence there would read as "new". */}
              {returning === 'returning' ? <p data-testid="search-quick-returning">Returning patient · registration on file</p> : null}
              {returning === 'unknown' ? <p data-testid="search-quick-returning-unknown">Earlier visits not fully listed here — the check-in will confirm</p> : null}
              {!visitReadError && activeVisits?.length === 0 ? <p>No appointment selected.</p> : null}
              {visitReadError ? <p role="alert">Current visit unavailable. <AcButton variant="ghost" onClick={() => setVisitRetry(n => n + 1)}>Retry visit</AcButton></p> : activeVisits === null ? <p role="status">Checking current visit…</p> : activeVisits.map(visit => <div key={visit.appointmentId} className="aps-quick-visit">
                <p>
                  {visit.consultStarted ? 'In consult' : dayStatusChip(visit.status).label}
                  {' · '}<AcButton variant="ghost" onClick={() => onAppointment(visit.appointmentId, visit.scheduledAt)}>Open appointment #{visit.appointmentId}</AcButton>
                </p>
                {returning !== 'new' && !returningBlockedByIntake(visit) && !visit.consultStarted && (visit.status === 'arrived' || visit.status === 'booked' || visit.status === 'confirmed') ? <AcButton
                  variant="primary"
                  data-testid={`search-quick-${visit.status === 'arrived' ? 'checkin' : 'arrived'}-${visit.appointmentId}`}
                  aria-label={`${visit.status === 'arrived' ? 'Check in' : 'Mark arrived'} ${patient.fullName}, appointment #${visit.appointmentId}`}
                  disabled={personSaving(visitSaving, patient.patientId) || busy}
                  onClick={() => visitAction(visit, visit.status === 'arrived' ? 'checked_in' : 'arrived')}
                >
                  {visitSaving.has(savingKey(patient.patientId, visit.appointmentId)) ? 'Saving…' : visit.status === 'arrived' ? 'Check in' : 'Mark arrived'}
                </AcButton> : null}
              </div>)}
              {visitNotice ? <p role={visitNotice.tone === 'error' ? 'alert' : 'status'} data-testid="search-quick-visit-notice">{visitNotice.text}</p> : null}
              <div className="aps-quick-primary-actions">
                {activeVisits === null || activeVisits.length === 0 ? <AcButton
                  variant="primary"
                  disabled={patient.status !== 'active' || activeVisits === null || visitReadError}
                  onClick={() => setArrival('arrival')}
                >
                  Record arrival
                </AcButton> : null}
                <AcButton
                  onClick={() => {
                    close();
                    setOpen(true);
                  }}
                >
                  Back to search
                </AcButton>
              </div>
              <div className="today-home__quick-actions">
                <AcButton disabled={patient.status !== 'active'} onClick={() => setArrival('appointment')}>Book appointment</AcButton>
                {(['visits', 'payments'] as const).map(detail => <AcButton key={detail} variant="ghost"
                  data-testid={`search-quick-${detail}`} onClick={() => setHistory(detail)}>
                  {detail === 'visits' ? 'All visits' : 'All payments'}</AcButton>)}
                {canOpenFullRecord ? <Link to={deskRecordHref(patient.patientId, recordOrigin, window.location.search)}>Open full record</Link> : null}

              </div>
            </section>
          </>
        )}
      </WorkPanel>
    ) : null;
  return (
    <>
      <SharedRecordSearch
        queryHint={receptionSearchQueryHint}
        query={query}
        onQueryChange={setQuery}
        scopeKey={scopeKey}
        clinicLabel={clinicLabel}
        scopeDescription={receptionSearchScopeLine(clinicLabel)}
        disabled={disabled || busy}
        open={open}
        onOpenChange={setOpen}
        load={load}
        onPick={pick}
        onRegister={(q) => setRegistration({ query: q })}
      />
      {panelTarget && panel ? createPortal(panel, panelTarget) : null}
      {registration ? (
        <GlobalPatientSearchModal
          key={registration.query + registration.leadId}
          initialQuery={registration.query}
          registrationIntent
          continueLeadId={registration.leadId}
          initialSearchPage={registration.page ?? 0}
          recordOrigin={recordOrigin}
          walkinClinicId={clinicId}
          onClose={() => {
            setRegistration(null);
            onChanged();
            focusHeader();
          }}
        />
      ) : null}
    </>
  );
}
